import moment from 'moment';
import {
  DIMENSION_CARTAO,
  DIMENSION_GERAL,
  DIMENSION_INSPECAO,
  DIMENSION_TIPO_PAGAMENTO,
  DIMENSION_TO_API_FILTER_KEY,
  DIMENSION_TO_SEGMENTOS_KEY,
  SEGMENT_ALL,
  STATIC_SEGMENT_OPTIONS,
  getSegmentFilterLabel,
  isSegmentFilterActive,
} from './segmentConstants';

export const VALIDATE_SCOPE_VALIDADO = 'VALIDADO';
export const VALIDATE_SCOPE_VALIDADO_PENDENTE = 'VALIDADO_PENDENTE';
export const VALIDATE_SCOPE_TODOS = 'TODOS';

export const VALIDATE_SCOPE_OPTIONS = [
  { id: VALIDATE_SCOPE_VALIDADO, nome: 'Validado' },
  { id: VALIDATE_SCOPE_VALIDADO_PENDENTE, nome: 'Validado + Aguardando' },
  { id: VALIDATE_SCOPE_TODOS, nome: 'Todos' },
];

export const TIPO_NOTA_DEFS = [
  { enum: 'ABASTECIMENTO', kpiKey: 'abastecimento', label: 'Abastecimento' },
  { enum: 'ADIANTAMENTO', kpiKey: 'adiantamento', label: 'Adiantamento' },
  { enum: 'ALIMENTACAO', kpiKey: 'alimentacao', label: 'Alimentação' },
  { enum: 'CORREIO', kpiKey: 'correio', label: 'Correio' },
  { enum: 'HOSPEDAGEM', kpiKey: 'hospedagem', label: 'Hospedagem' },
  { enum: 'MANUTENCAO_VEICULO', kpiKey: 'manutencaoVeiculo', label: 'Manutenção do Veículo' },
  { enum: 'COMPRA_MANUTENCAO_VEICULAR', kpiKey: 'compraManutencaoVeicular', label: 'Compra Man. Veicular' },
  { enum: 'PEDAGIO', kpiKey: 'pedagio', label: 'Pedágio/Balça' },
  { enum: 'OUTROS', kpiKey: 'outros', label: 'Outros' },
];

export const TIPO_NOTA_BY_ENUM = TIPO_NOTA_DEFS.reduce((acc, def) => {
  acc[def.enum] = def;
  return acc;
}, {});

export const TIPO_NOTA_BY_KPI_KEY = TIPO_NOTA_DEFS.reduce((acc, def) => {
  acc[def.kpiKey] = def;
  return acc;
}, {});

const LABEL_COLORS = {
  Abastecimento: '#1976d2',
  Adiantamento: '#0288d1',
  'Alimentação': '#2e7d32',
  Correio: '#5c6bc0',
  Hospedagem: '#7b1fa2',
  'Manutenção do Veículo': '#ed6c02',
  'Compra Man. Veicular': '#f57c00',
  'Pedágio/Balça': '#00838f',
  Outros: '#757575',
};

/** Cores indexadas por label, enum e kpiKey (API pode enviar qualquer um). */
export const CATEGORIA_CHART_COLORS = TIPO_NOTA_DEFS.reduce((acc, def) => {
  const color = LABEL_COLORS[def.label] || '#757575';
  acc[def.label] = color;
  acc[def.enum] = color;
  acc[def.kpiKey] = color;
  return acc;
}, {});

const stripAccents = (value) =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * Resolve TipoNota a partir de enum (ABASTECIMENTO), kpiKey (abastecimento) ou label.
 * O backend do BI costuma enviar kpiKey em `categoria` / `distribuicao`.
 */
export const resolveTipoNotaDef = (tipoNota, categoria) => {
  const tryMatch = (raw) => {
    if (raw == null || raw === '') return null;
    const str = String(raw).trim();
    if (!str) return null;

    if (TIPO_NOTA_BY_ENUM[str]) return TIPO_NOTA_BY_ENUM[str];

    const upper = str.toUpperCase();
    if (TIPO_NOTA_BY_ENUM[upper]) return TIPO_NOTA_BY_ENUM[upper];

    if (TIPO_NOTA_BY_KPI_KEY[str]) return TIPO_NOTA_BY_KPI_KEY[str];

    const lower = str.toLowerCase();
    if (TIPO_NOTA_BY_KPI_KEY[lower]) return TIPO_NOTA_BY_KPI_KEY[lower];

    const norm = stripAccents(lower);
    const byLabel = TIPO_NOTA_DEFS.find(
      (d) => stripAccents(d.label.toLowerCase()) === norm,
    );
    if (byLabel) return byLabel;

    return null;
  };

  return (
    tryMatch(tipoNota)
    || tryMatch(categoria)
    || TIPO_NOTA_BY_ENUM.OUTROS
  );
};

export const getCategoriaChartColor = (categoria, tipoNota) => {
  const def = resolveTipoNotaDef(tipoNota, categoria);
  return CATEGORIA_CHART_COLORS[def.kpiKey] || '#757575';
};

export const FILTERABLE_CATEGORIAS = TIPO_NOTA_DEFS.map((d) => d.label);

export const CATEGORIA_STAT_COLORS = {
  Abastecimento: 'info',
  Adiantamento: 'info',
  'Alimentação': 'success',
  Correio: 'default',
  Hospedagem: 'default',
  'Manutenção do Veículo': 'warning',
  'Compra Man. Veicular': 'warning',
  'Pedágio/Balça': 'info',
  Outros: 'default',
};

export const EVOLUCAO_SERIES_KEYS = TIPO_NOTA_DEFS.map(({ kpiKey, label }) => ({
  key: kpiKey,
  label,
}));

/** Mesmas 9 categorias da Evolução — evita "Outros" residual nos gráficos de ranking/segmento. */
export const RANKING_SERIES_KEYS = EVOLUCAO_SERIES_KEYS;

const EMPTY_KPI_CATEGORIES = TIPO_NOTA_DEFS.reduce((acc, { kpiKey }) => {
  acc[kpiKey] = 0;
  return acc;
}, {});

export const EMPTY_KPIS = {
  totalGasto: 0,
  viaAporte: 0,
  viaCartao: 0,
  quantidadeNotas: 0,
  ticketMedio: 0,
  percentualInspecao: null,
  ...EMPTY_KPI_CATEGORIES,
};

export const EMPTY_SEGMENTOS = {
  tipoPagamento: [],
  inspecao: [],
  cartao: [],
};

export const EMPTY_REPORT = {
  usuario: null,
  kpis: EMPTY_KPIS,
  evolucaoMensal: [],
  distribuicao: [],
  transacoes: [],
  rankingUsuarios: [],
  segmentos: EMPTY_SEGMENTOS,
};

export const COMPANY_SCOPE_LABEL = 'Empresa (todos os colaboradores)';

export const getDefaultFilter = () => ({
  usuarioId: null,
  inicio: moment().startOf('year').valueOf(),
  fim: moment().endOf('day').valueOf(),
  dim: DIMENSION_GERAL,
  seg: SEGMENT_ALL,
  validateScope: VALIDATE_SCOPE_VALIDADO,
  tipoPagamento: null,
  cartao: null,
  inspecao: null,
});

export const getScopeLabel = (usuarioOrNome) => {
  const nome =
    usuarioOrNome != null && typeof usuarioOrNome === 'object'
      ? usuarioOrNome.nome
      : usuarioOrNome;
  return nome ? `Colaborador: ${nome}` : COMPANY_SCOPE_LABEL;
};

export const isCompanyScope = (filter) => !filter?.usuarioId;

export const formatPeriodLabel = (inicio, fim) => {
  if (inicio == null || fim == null) return '';
  const start = moment(inicio).format('DD/MM/YYYY');
  const end = moment(fim).format('DD/MM/YYYY');
  return `${start} – ${end}`;
};

export const formatMesLabel = (mes) => {
  if (!mes) return '—';
  const parsed = moment(mes, 'YYYY-MM', true);
  if (!parsed.isValid()) return mes;
  return parsed.format('MMM/YYYY');
};

export const formatBRL = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

export const formatPercent = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
};

export const getTipoNotaLabel = (tipoNota, categoria) =>
  resolveTipoNotaDef(tipoNota, categoria)?.label || 'Outros';

export const getTipoPagamentoLabel = (tipo) => {
  if (tipo === 'CARTAO_CORPORATIVO') return 'Cartão Corporativo';
  if (tipo === 'APORTE') return 'Aporte';
  return '—';
};

export const includesPendingValidation = (validateScope) =>
  validateScope === VALIDATE_SCOPE_VALIDADO_PENDENTE
  || validateScope === VALIDATE_SCOPE_TODOS;

export const normalizeFilterForApi = (filter) => {
  if (!filter || typeof filter !== 'object') return null;

  const body = {
    inicio: filter.inicio ?? null,
    fim: filter.fim ?? null,
    validate: filter.validateScope || VALIDATE_SCOPE_VALIDADO,
  };

  if (filter.usuarioId) {
    body.usuarioId = filter.usuarioId;
    if (filter.tipoPagamento) body.tipoPagamentos = [filter.tipoPagamento];
    if (filter.cartao) body.cartaoIds = [filter.cartao];
    if (filter.inspecao != null && filter.inspecao !== '') {
      body.inspecao = filter.inspecao === true || String(filter.inspecao) === 'true';
    }
    return body;
  }

  const dim = filter.dim || DIMENSION_GERAL;
  const seg = filter.seg || SEGMENT_ALL;
  if (isSegmentFilterActive(dim, seg)) {
    const apiKey = DIMENSION_TO_API_FILTER_KEY[dim];
    if (apiKey === 'tipoPagamentos') {
      body.tipoPagamentos = [seg];
    } else if (apiKey === 'inspecao') {
      body.inspecao = seg === 'true';
    } else if (apiKey === 'cartaoIds') {
      body.cartaoIds = [seg];
    }
  }

  if (filter.tipoPagamento) body.tipoPagamentos = [filter.tipoPagamento];
  if (filter.cartao) body.cartaoIds = [filter.cartao];
  if (filter.inspecao != null && filter.inspecao !== '') {
    body.inspecao = filter.inspecao === true || String(filter.inspecao) === 'true';
  }

  return body;
};

const normalizeUsuario = (data) => {
  if (!data || typeof data !== 'object') return null;
  return {
    id: data.id ?? null,
    nome: data.nome ?? null,
    email: data.email ?? null,
  };
};

const normalizeSegmentItem = (item) => ({
  key: item.key ?? item.id ?? null,
  label: item.label ?? item.nome ?? '—',
  total: Number(item.total) || 0,
  percentual: item.percentual != null ? Number(item.percentual) : null,
  quantidadeUsuarios: Number(item.quantidadeUsuarios) || 0,
  quantidadeNotas: Number(item.quantidadeNotas) || 0,
  ...TIPO_NOTA_DEFS.reduce((acc, { kpiKey }) => {
    acc[kpiKey] = Number(item[kpiKey]) || 0;
    return acc;
  }, {}),
});

const normalizeSegmentos = (segmentos) => {
  if (!segmentos || typeof segmentos !== 'object') {
    return { ...EMPTY_SEGMENTOS };
  }
  return {
    tipoPagamento: Array.isArray(segmentos.tipoPagamento)
      ? segmentos.tipoPagamento.map(normalizeSegmentItem)
      : [],
    inspecao: Array.isArray(segmentos.inspecao)
      ? segmentos.inspecao.map(normalizeSegmentItem)
      : [],
    cartao: Array.isArray(segmentos.cartao)
      ? segmentos.cartao.map(normalizeSegmentItem)
      : [],
  };
};

const normalizeRankingUsuarioItem = (item) => ({
  usuarioId: item.usuarioId ?? null,
  nome: item.nome ?? item.usuarioNome ?? '—',
  total: Number(item.total) || 0,
  viaAporte: Number(item.viaAporte) || 0,
  viaCartao: Number(item.viaCartao) || 0,
  quantidadeNotas: Number(item.quantidadeNotas) || 0,
  ...TIPO_NOTA_DEFS.reduce((acc, { kpiKey }) => {
    acc[kpiKey] = Number(item[kpiKey]) || 0;
    return acc;
  }, {}),
});

const normalizeTransacaoRow = (row) => {
  const tipoRaw = row.tipoNota ?? row.tipo ?? row.tipoLabel ?? null;
  const def = resolveTipoNotaDef(tipoRaw, row.categoria ?? row.tipoLabel);
  return {
    ...row,
    tipoNota: def.enum,
    categoria: def.label,
    descricao: row.descricao ?? row.justificativa ?? null,
    cartaoNumero: row.cartaoNumero ?? row.cartaoCodigo ?? null,
  };
};

const normalizeDistribuicaoItem = (item) => {
  const def = resolveTipoNotaDef(item.tipoNota, item.categoria);
  return {
    ...item,
    tipoNota: def.enum,
    categoria: def.label,
  };
};

export const hasRealCategoryBreakdown = (row) => {
  if (!row) return false;
  return TIPO_NOTA_DEFS.some(
    ({ kpiKey }) => kpiKey !== 'outros' && (Number(row[kpiKey]) || 0) > 0,
  );
};

const mergeRankingUsuarioWithTransacoes = (apiRow, txRow) => {
  if (!txRow) return apiRow;

  const apiHasBreakdown = hasRealCategoryBreakdown(apiRow);
  const txHasBreakdown = hasRealCategoryBreakdown(txRow);

  if (txHasBreakdown) {
    return {
      ...apiRow,
      ...txRow,
      nome: apiRow.nome || txRow.nome,
      total: txRow.total || apiRow.total,
    };
  }

  if (apiHasBreakdown) {
    return {
      ...txRow,
      ...apiRow,
      nome: apiRow.nome || txRow.nome,
      total: apiRow.total || txRow.total,
    };
  }

  return {
    ...apiRow,
    total: apiRow.total || txRow.total,
    quantidadeNotas: apiRow.quantidadeNotas || txRow.quantidadeNotas,
    viaAporte: apiRow.viaAporte || txRow.viaAporte,
    viaCartao: apiRow.viaCartao || txRow.viaCartao,
  };
};

export const normalizeBiResponse = (data) => {
  if (!data || typeof data !== 'object') {
    return EMPTY_REPORT;
  }

  const kpis = data.kpis && typeof data.kpis === 'object' ? data.kpis : {};
  const transacoes = Array.isArray(data.transacoes)
    ? data.transacoes.map(normalizeTransacaoRow)
    : [];
  const rankingUsuarios = Array.isArray(data.rankingUsuarios)
    ? data.rankingUsuarios.map(normalizeRankingUsuarioItem)
    : [];

  return {
    usuario: normalizeUsuario(data.usuario),
    kpis: {
      ...EMPTY_KPIS,
      totalGasto: Number(kpis.totalGasto) || 0,
      viaAporte: Number(kpis.viaAporte) || 0,
      viaCartao: Number(kpis.viaCartao) || 0,
      quantidadeNotas: Number(kpis.quantidadeNotas) || 0,
      ticketMedio: Number(kpis.ticketMedio) || 0,
      percentualInspecao: kpis.percentualInspecao != null
        ? Number(kpis.percentualInspecao)
        : null,
      ...TIPO_NOTA_DEFS.reduce((acc, { kpiKey }) => {
        acc[kpiKey] = Number(kpis[kpiKey]) || 0;
        return acc;
      }, {}),
    },
    evolucaoMensal: Array.isArray(data.evolucaoMensal) ? data.evolucaoMensal : [],
    distribuicao: Array.isArray(data.distribuicao)
      ? data.distribuicao.map(normalizeDistribuicaoItem)
      : [],
    transacoes,
    rankingUsuarios,
    segmentos: normalizeSegmentos(data.segmentos),
  };
};

export const mapEvolucaoToDataset = (items) =>
  (items || []).map((item) => {
    const row = { mes: item.mes, mesLabel: formatMesLabel(item.mes) };
    TIPO_NOTA_DEFS.forEach(({ kpiKey }) => {
      row[kpiKey] = Number(item[kpiKey]) || 0;
    });
    return row;
  });

export const evolucaoHasData = (dataset) =>
  (dataset || []).some((row) =>
    EVOLUCAO_SERIES_KEYS.some(({ key }) => Number(row[key]) > 0),
  );

export const buildEvolucaoStackedSeries = () =>
  EVOLUCAO_SERIES_KEYS.map(({ key, label }) => ({
    id: key,
    dataKey: key,
    label,
    stack: 'gastos',
    color: CATEGORIA_CHART_COLORS[label],
    valueFormatter: (value) => formatBRL(value),
  }));

export const RANKING_TOP_N = 10;
export const PARETO_THRESHOLD = 80;

const normalizeTipoNotaKey = (tipoNota, categoria) =>
  resolveTipoNotaDef(tipoNota, categoria)?.kpiKey || 'outros';

const emptyRankingRow = (usuarioId, nome) => ({
  usuarioId,
  nome,
  total: 0,
  viaAporte: 0,
  viaCartao: 0,
  quantidadeNotas: 0,
  ...TIPO_NOTA_DEFS.reduce((acc, { kpiKey }) => {
    acc[kpiKey] = 0;
    return acc;
  }, {}),
});

export const buildRankingFromTransacoes = (transacoes) => {
  const byUsuario = {};

  (transacoes || []).forEach((row) => {
    const usuarioId = row.usuarioId;
    const nome = row.usuarioNome || '—';
    if (!usuarioId) return;

    const catKey = normalizeTipoNotaKey(row.tipoNota, row.categoria);
    const valor = Number(row.valor) || 0;
    if (valor <= 0) return;

    if (!byUsuario[usuarioId]) {
      byUsuario[usuarioId] = emptyRankingRow(usuarioId, nome);
    }

    byUsuario[usuarioId][catKey] += valor;
    byUsuario[usuarioId].total += valor;
    byUsuario[usuarioId].quantidadeNotas += 1;
    if (row.tipoPagamento === 'APORTE') {
      byUsuario[usuarioId].viaAporte += valor;
    } else if (row.tipoPagamento === 'CARTAO_CORPORATIVO') {
      byUsuario[usuarioId].viaCartao += valor;
    }
  });

  return Object.values(byUsuario).sort((a, b) => b.total - a.total);
};

export const getTopRanking = (ranking, limit = RANKING_TOP_N) =>
  (ranking || []).slice(0, limit);

export const computeParetoInsight = (ranking, totalGasto) => {
  const total = Number(totalGasto) || 0;
  const users = ranking || [];
  const totalUsers = users.length;

  if (total <= 0 || totalUsers === 0) {
    return { topCount: 0, topPercent: 0, totalUsers: 0, totalGasto: total };
  }

  let cumulative = 0;
  let topCount = 0;

  for (let i = 0; i < users.length; i += 1) {
    cumulative += users[i].total;
    topCount = i + 1;
    if ((cumulative / total) * 100 >= PARETO_THRESHOLD) break;
  }

  return {
    topCount,
    topPercent: (cumulative / total) * 100,
    totalUsers,
    totalGasto: total,
  };
};

export const resolveRanking = (rankingUsuarios, transacoes) => {
  const fromTx = buildRankingFromTransacoes(transacoes);
  const txById = {};
  fromTx.forEach((row) => {
    if (row.usuarioId) txById[row.usuarioId] = row;
  });

  if (!Array.isArray(rankingUsuarios) || rankingUsuarios.length === 0) {
    return fromTx;
  }

  const merged = [];
  const seen = new Set();

  rankingUsuarios.forEach((item) => {
    const apiRow = normalizeRankingUsuarioItem(item);
    const txRow = apiRow.usuarioId ? txById[apiRow.usuarioId] : null;
    const next = txRow
      ? mergeRankingUsuarioWithTransacoes(apiRow, txRow)
      : apiRow;
    merged.push(next);
    if (apiRow.usuarioId) seen.add(apiRow.usuarioId);
  });

  fromTx.forEach((txRow) => {
    if (txRow.usuarioId && !seen.has(txRow.usuarioId)) {
      merged.push(txRow);
    }
  });

  return merged.sort((a, b) => b.total - a.total);
};

export const mapRankingToChartDataset = (ranking) =>
  getTopRanking(ranking)
    .filter((item) => Number(item.total) > 0)
    .map((item) => {
      const chartRow = {
        label: item.nome || '—',
        usuarioId: item.usuarioId,
        total: item.total,
      };
      TIPO_NOTA_DEFS.forEach(({ kpiKey }) => {
        chartRow[kpiKey] = Number(item[kpiKey]) || 0;
      });
      return chartRow;
    });

export const buildRankingStackedSeries = () =>
  RANKING_SERIES_KEYS.map(({ key, label }) => ({
    id: key,
    dataKey: key,
    label,
    stack: 'gastos',
    color: CATEGORIA_CHART_COLORS[label],
    valueFormatter: (value) => formatBRL(value),
  }));

export const filterSegmentsWithCost = (items) =>
  (items || []).filter((item) => Number(item.total) > 0);

const emptySegmentAggregate = (key, label) => ({
  key,
  label,
  total: 0,
  percentual: null,
  quantidadeUsuarios: 0,
  quantidadeNotas: 0,
  ...TIPO_NOTA_DEFS.reduce((acc, { kpiKey }) => {
    acc[kpiKey] = 0;
    return acc;
  }, {}),
});

const getSegmentKeyAndLabel = (row, dimension) => {
  if (dimension === DIMENSION_TIPO_PAGAMENTO) {
    const segKey = row.tipoPagamento;
    if (!segKey) return null;
    return { segKey, label: getTipoPagamentoLabel(segKey) };
  }

  if (dimension === DIMENSION_INSPECAO) {
    const isInsp = row.inspecao === true || row.inspecao === 'true';
    const segKey = String(isInsp);
    return { segKey, label: isInsp ? 'Inspeção' : 'Outros' };
  }

  if (dimension === DIMENSION_CARTAO) {
    if (row.tipoPagamento !== 'CARTAO_CORPORATIVO') return null;
    const segKey = row.cartaoId ?? row.idCartao ?? row.cartao ?? null;
    if (!segKey) return null;
    const label = row.cartaoNumero || row.cartaoCodigo || row.cartaoNome || `Cartão ${segKey}`;
    return { segKey: String(segKey), label };
  }

  return null;
};

/** Agrega transações por dimensão de segmento (fallback quando API não envia segmentos). */
export const buildSegmentOptionsFromTransacoes = (transacoes, dimension) => {
  if (!transacoes?.length || !dimension) return [];

  const byKey = {};
  const usuariosPorSegmento = {};

  transacoes.forEach((row) => {
    const segment = getSegmentKeyAndLabel(row, dimension);
    if (!segment) return;

    const valor = Number(row.valor) || 0;
    if (valor <= 0) return;

    const { segKey, label } = segment;
    if (!byKey[segKey]) {
      byKey[segKey] = emptySegmentAggregate(segKey, label);
      usuariosPorSegmento[segKey] = new Set();
    }

    const def = resolveTipoNotaDef(row.tipoNota, row.categoria);
    byKey[segKey][def.kpiKey] += valor;
    byKey[segKey].total += valor;
    byKey[segKey].quantidadeNotas += 1;
    if (row.usuarioId) usuariosPorSegmento[segKey].add(row.usuarioId);
  });

  return Object.values(byKey)
    .map((item) => ({
      ...item,
      quantidadeUsuarios: usuariosPorSegmento[item.key]?.size || 0,
    }))
    .sort((a, b) => b.total - a.total);
};

const mergeSegmentWithTransacoes = (apiRow, txRow) => {
  if (!txRow) return apiRow;

  const apiHasBreakdown = hasRealCategoryBreakdown(apiRow);
  const txHasBreakdown = hasRealCategoryBreakdown(txRow);

  if (txHasBreakdown) {
    return {
      ...apiRow,
      ...txRow,
      label: apiRow.label || txRow.label,
      total: txRow.total || apiRow.total,
      quantidadeNotas: txRow.quantidadeNotas || apiRow.quantidadeNotas,
      quantidadeUsuarios: txRow.quantidadeUsuarios || apiRow.quantidadeUsuarios,
    };
  }

  if (apiHasBreakdown) {
    return {
      ...txRow,
      ...apiRow,
      label: apiRow.label || txRow.label,
      total: apiRow.total || txRow.total,
    };
  }

  return {
    ...apiRow,
    total: apiRow.total || txRow.total,
    quantidadeNotas: apiRow.quantidadeNotas || txRow.quantidadeNotas,
    quantidadeUsuarios: apiRow.quantidadeUsuarios || txRow.quantidadeUsuarios,
  };
};

const mergeApiSegmentsWithTransacoes = (fromApi, fromTransacoes) => {
  if (!fromTransacoes?.length) return fromApi;

  const txByKey = {};
  fromTransacoes.forEach((row) => {
    if (row.key != null) txByKey[String(row.key)] = row;
  });

  const merged = fromApi.map((item) =>
    mergeSegmentWithTransacoes(item, txByKey[String(item.key)]));

  const seen = new Set(fromApi.map((item) => String(item.key)));
  fromTransacoes.forEach((txRow) => {
    if (txRow.key != null && !seen.has(String(txRow.key))) {
      merged.push(txRow);
    }
  });

  return merged.sort((a, b) => b.total - a.total);
};

const mergeWithStaticSegmentOptions = (dynamic, staticList) => {
  const byKey = {};
  (dynamic || []).forEach((item) => {
    if (item.key != null) byKey[String(item.key)] = item;
  });

  return staticList.map((opt) => {
    const existing = byKey[String(opt.key)];
    if (existing) return existing;
    return emptySegmentAggregate(opt.key, opt.label);
  });
};

export const resolveSegmentChipOptions = ({
  dimension,
  apiSegmentos,
  cacheOptions,
  transacoes,
}) => {
  const segmentKey = DIMENSION_TO_SEGMENTOS_KEY[dimension];
  const fromApi = (cacheOptions && cacheOptions.length > 0)
    ? cacheOptions
    : (apiSegmentos?.[segmentKey] || []);

  const fromTransacoes = buildSegmentOptionsFromTransacoes(transacoes, dimension);

  if (fromApi.length > 0) {
    const merged = mergeApiSegmentsWithTransacoes(fromApi, fromTransacoes);
    const staticList = STATIC_SEGMENT_OPTIONS[dimension];
    if (staticList?.length) {
      return mergeWithStaticSegmentOptions(merged, staticList);
    }
    return merged;
  }

  const dynamic = fromTransacoes.length > 0 ? fromTransacoes : [];
  const staticList = STATIC_SEGMENT_OPTIONS[dimension];

  if (staticList?.length) {
    return mergeWithStaticSegmentOptions(dynamic, staticList);
  }

  return dynamic;
};

export const mapSegmentToChartDataset = (items) =>
  filterSegmentsWithCost(items).map((item) => {
    const chartRow = {
      label: item.label || '—',
      key: item.key,
      total: item.total,
    };
    TIPO_NOTA_DEFS.forEach(({ kpiKey }) => {
      chartRow[kpiKey] = Number(item[kpiKey]) || 0;
    });
    return chartRow;
  });

const emptyEvolucaoMonth = (mes) => ({
  mes,
  ...TIPO_NOTA_DEFS.reduce((acc, { kpiKey }) => {
    acc[kpiKey] = 0;
    return acc;
  }, {}),
});

const getTransactionMonth = (row) => {
  if (row?.data == null) return null;
  const parsed = moment(row.data);
  return parsed.isValid() ? parsed.format('YYYY-MM') : null;
};

export const matchesCategoriaLabel = (row, categoriaLabel) => {
  if (!categoriaLabel || !row) return false;
  return (
    row.categoria === categoriaLabel
    || getTipoNotaLabel(row.tipoNota, row.categoria) === categoriaLabel
  );
};

export const filterTransacoesByCategoria = (transacoes, categoriaLabel) => {
  if (!categoriaLabel) return transacoes || [];
  return (transacoes || []).filter((row) => matchesCategoriaLabel(row, categoriaLabel));
};

/**
 * Escopo base da aba de dimensão (antes do chip de segmento).
 * Na aba Cartão, mesmo com "Todos", exibe só notas de cartão corporativo.
 */
export const filterTransacoesByDimensionScope = (transacoes, dimension) => {
  if (!dimension || dimension === DIMENSION_GERAL) return transacoes || [];

  if (dimension === DIMENSION_CARTAO) {
    return (transacoes || []).filter(
      (row) => row.tipoPagamento === 'CARTAO_CORPORATIVO',
    );
  }

  return transacoes || [];
};

const matchesSegmentFilter = (row, dimension, seg) => {
  const segment = getSegmentKeyAndLabel(row, dimension);
  if (!segment) return false;
  return String(segment.segKey) === String(seg);
};

/** Filtra transações pelo chip de segmento ativo (cartão, tipo pagamento, inspeção). */
export const filterTransacoesBySegment = (transacoes, dimension, seg) => {
  if (!isSegmentFilterActive(dimension, seg)) return transacoes || [];
  return (transacoes || []).filter((row) => matchesSegmentFilter(row, dimension, seg));
};

export const buildKpisFromTransacoes = (transacoes) => {
  const kpis = { ...EMPTY_KPIS };
  let inspecaoTotal = 0;

  (transacoes || []).forEach((row) => {
    const valor = Number(row.valor) || 0;
    if (valor <= 0) return;

    const def = resolveTipoNotaDef(row.tipoNota, row.categoria);
    kpis[def.kpiKey] += valor;
    kpis.totalGasto += valor;
    kpis.quantidadeNotas += 1;

    if (row.tipoPagamento === 'APORTE') {
      kpis.viaAporte += valor;
    } else if (row.tipoPagamento === 'CARTAO_CORPORATIVO') {
      kpis.viaCartao += valor;
    }

    if (row.inspecao === true || row.inspecao === 'true') {
      inspecaoTotal += valor;
    }
  });

  kpis.ticketMedio = kpis.quantidadeNotas > 0
    ? kpis.totalGasto / kpis.quantidadeNotas
    : 0;
  kpis.percentualInspecao = kpis.totalGasto > 0
    ? (inspecaoTotal / kpis.totalGasto) * 100
    : null;

  return kpis;
};

export const buildDistribuicaoFromTransacoes = (transacoes) => {
  const byKey = {};
  let total = 0;

  (transacoes || []).forEach((row) => {
    const valor = Number(row.valor) || 0;
    if (valor <= 0) return;

    const def = resolveTipoNotaDef(row.tipoNota, row.categoria);
    if (!byKey[def.kpiKey]) {
      byKey[def.kpiKey] = {
        categoria: def.label,
        tipoNota: def.enum,
        valor: 0,
      };
    }
    byKey[def.kpiKey].valor += valor;
    total += valor;
  });

  return Object.values(byKey)
    .map((item) => ({
      ...item,
      percentual: total > 0 ? (item.valor / total) * 100 : 0,
    }))
    .sort((a, b) => b.valor - a.valor);
};

export const buildEvolucaoMensalFromTransacoes = (transacoes, inicio, fim) => {
  const byMonth = {};

  (transacoes || []).forEach((row) => {
    const mes = getTransactionMonth(row);
    const valor = Number(row.valor) || 0;
    if (!mes || valor <= 0) return;

    if (!byMonth[mes]) {
      byMonth[mes] = emptyEvolucaoMonth(mes);
    }

    const def = resolveTipoNotaDef(row.tipoNota, row.categoria);
    byMonth[mes][def.kpiKey] += valor;
  });

  const start = inicio != null ? moment(inicio).startOf('month') : null;
  const end = fim != null ? moment(fim).endOf('month') : null;

  if (!start?.isValid() || !end?.isValid()) {
    return Object.keys(byMonth)
      .sort()
      .map((mes) => byMonth[mes]);
  }

  const result = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, 'month')) {
    const mes = cursor.format('YYYY-MM');
    result.push(byMonth[mes] || emptyEvolucaoMonth(mes));
    cursor.add(1, 'month');
  }

  return result;
};

/**
 * Deriva KPIs, distribuição e evolução a partir de transacoes quando disponíveis.
 * Fallback para payload da API quando a lista de transações está vazia.
 */
export const resolveReportMetrics = (reportData, inicio, fim) => {
  const transacoes = reportData?.transacoes || [];
  if (transacoes.length > 0) {
    return {
      kpis: buildKpisFromTransacoes(transacoes),
      distribuicao: buildDistribuicaoFromTransacoes(transacoes),
      evolucaoMensal: buildEvolucaoMensalFromTransacoes(transacoes, inicio, fim),
    };
  }

  return {
    kpis: reportData?.kpis && typeof reportData.kpis === 'object'
      ? reportData.kpis
      : { ...EMPTY_KPIS },
    distribuicao: Array.isArray(reportData?.distribuicao) ? reportData.distribuicao : [],
    evolucaoMensal: Array.isArray(reportData?.evolucaoMensal) ? reportData.evolucaoMensal : [],
  };
};

/**
 * Monta itens para cards de categoria (KpiStrip): valor, percentual, cor e ordenação.
 */
export const buildCategoryCardItems = (kpis, options = {}) => {
  const { hideZero = true, sort = 'desc' } = options;
  const totalGasto = Number(kpis?.totalGasto) || 0;

  let items = TIPO_NOTA_DEFS.map((def) => {
    const valor = Number(kpis?.[def.kpiKey]) || 0;
    return {
      kpiKey: def.kpiKey,
      label: def.label,
      enum: def.enum,
      valor,
      percentual: totalGasto > 0 ? (valor / totalGasto) * 100 : 0,
      color: CATEGORIA_STAT_COLORS[def.label] || 'default',
    };
  });

  if (hideZero) {
    items = items.filter((item) => item.valor > 0);
  }

  if (sort === 'desc') {
    items.sort((a, b) => b.valor - a.valor);
  } else if (sort === 'asc') {
    items.sort((a, b) => a.valor - b.valor);
  }

  return items;
};

/** Grid responsivo conforme quantidade de cards de categoria visíveis. */
export const getCategoryGridSize = (count) => {
  if (count <= 0) return { xs: 12 };
  if (count <= 2) return { xs: 12, sm: 6, md: 6 };
  if (count <= 4) return { xs: 6, sm: 6, md: 3 };
  if (count <= 6) return { xs: 6, sm: 4, md: 2 };
  return { xs: 6, sm: 4, md: 3 };
};

export const isPeriodInvalid = (inicio, fim) => {
  if (inicio == null || fim == null) return false;
  return moment(inicio).isAfter(moment(fim));
};

const URL_DATE_FORMAT = 'YYYY-MM-DD';

const VALID_DIMENSIONS = new Set([
  DIMENSION_GERAL,
  DIMENSION_TIPO_PAGAMENTO,
  DIMENSION_INSPECAO,
  DIMENSION_CARTAO,
]);

const VALID_VALIDATE_SCOPES = new Set([
  VALIDATE_SCOPE_VALIDADO,
  VALIDATE_SCOPE_VALIDADO_PENDENTE,
  VALIDATE_SCOPE_TODOS,
]);

export const parseCategoriaFromSearch = (search) => {
  const q = new URLSearchParams(search);
  const raw = q.get('categoria');
  if (!raw) return null;

  const decoded = decodeURIComponent(raw);
  const match = TIPO_NOTA_DEFS.find((d) => d.label === decoded);
  return match?.label ?? null;
};

export const categoriaFilterEqual = (a, b) => (a || null) === (b || null);

export const parseFilterFromSearch = (search, defaultFilter) => {
  const q = new URLSearchParams(search);
  const filter = {
    ...defaultFilter,
    usuarioId: null,
  };

  const usuarioId = q.get('usuarioId');
  if (usuarioId) filter.usuarioId = usuarioId;

  const inicio = q.get('inicio');
  if (inicio) {
    const parsed = moment(inicio, URL_DATE_FORMAT, true);
    if (parsed.isValid()) filter.inicio = parsed.startOf('day').valueOf();
  }

  const fim = q.get('fim');
  if (fim) {
    const parsed = moment(fim, URL_DATE_FORMAT, true);
    if (parsed.isValid()) filter.fim = parsed.endOf('day').valueOf();
  }

  const dim = q.get('dim');
  if (dim && VALID_DIMENSIONS.has(dim)) filter.dim = dim;

  const seg = q.get('seg');
  if (seg) filter.seg = seg;

  const validate = q.get('validate');
  if (validate && VALID_VALIDATE_SCOPES.has(validate)) {
    filter.validateScope = validate;
  }

  return filter;
};

export const buildSearchFromFilter = (filter, activeCategoria = null) => {
  const q = new URLSearchParams();
  if (filter?.usuarioId) q.set('usuarioId', filter.usuarioId);
  if (filter?.inicio != null) {
    q.set('inicio', moment(filter.inicio).format(URL_DATE_FORMAT));
  }
  if (filter?.fim != null) {
    q.set('fim', moment(filter.fim).format(URL_DATE_FORMAT));
  }
  if (filter?.dim && filter.dim !== DIMENSION_GERAL) {
    q.set('dim', filter.dim);
    if (filter.seg && filter.seg !== SEGMENT_ALL) q.set('seg', filter.seg);
  }
  if (filter?.validateScope && filter.validateScope !== VALIDATE_SCOPE_VALIDADO) {
    q.set('validate', filter.validateScope);
  }
  if (activeCategoria) {
    q.set('categoria', activeCategoria);
  }
  return q.toString();
};

export const filtersEqual = (a, b) => {
  if (!a || !b) return false;
  return (
    (a.usuarioId || null) === (b.usuarioId || null) &&
    a.inicio === b.inicio &&
    a.fim === b.fim &&
    (a.dim || DIMENSION_GERAL) === (b.dim || DIMENSION_GERAL) &&
    (a.seg || SEGMENT_ALL) === (b.seg || SEGMENT_ALL) &&
    (a.validateScope || VALIDATE_SCOPE_VALIDADO) === (b.validateScope || VALIDATE_SCOPE_VALIDADO)
  );
};

export const hasFilterSearchParams = (search) =>
  Boolean(search && search.replace(/^\?/, '').trim().length > 0);

export const getRankingChartLayout = (dataset) => {
  const labels = (dataset || []).map((row) => String(row.label || '—'));
  const maxChars = labels.reduce((max, label) => Math.max(max, label.length), 4);
  const yAxisWidth = Math.min(140, Math.max(72, Math.ceil(maxChars * 6.5) + 8));
  return {
    yAxisWidth,
    margin: { left: 0, right: 132, top: 8, bottom: 32 },
  };
};

// --- Export PDF ---

export const SECAO_RELATORIO_FINANCEIRO = {
  KPIS: 'KPIS',
  DISTRIBUICAO: 'DISTRIBUICAO',
  EVOLUCAO_MENSAL: 'EVOLUCAO_MENSAL',
  RANKING: 'RANKING',
  SEGMENTOS: 'SEGMENTOS',
  DETALHADO: 'DETALHADO',
};

export const SECAO_RELATORIO_FINANCEIRO_OPTIONS = [
  {
    value: SECAO_RELATORIO_FINANCEIRO.KPIS,
    label: 'Indicadores (KPIs)',
    description: 'Total gasto, aporte/cartão, qtd. de notas, ticket médio e % inspeção.',
  },
  {
    value: SECAO_RELATORIO_FINANCEIRO.DISTRIBUICAO,
    label: 'Distribuição por categoria',
    description: 'Gasto por tipo de nota (abastecimento, alimentação…) com percentual do total.',
  },
  {
    value: SECAO_RELATORIO_FINANCEIRO.EVOLUCAO_MENSAL,
    label: 'Evolução mensal',
    description: 'Valores mês a mês, detalhados por categoria.',
  },
  {
    value: SECAO_RELATORIO_FINANCEIRO.RANKING,
    label: 'Ranking de usuários',
    description: 'Colaboradores ordenados por total gasto no período.',
    disableWhenUsuarioFilter: true,
  },
  {
    value: SECAO_RELATORIO_FINANCEIRO.SEGMENTOS,
    label: 'Segmentos',
    description: 'Comparativo por forma de pagamento, inspeção e cartão corporativo.',
    disableWhenUsuarioFilter: true,
  },
  {
    value: SECAO_RELATORIO_FINANCEIRO.DETALHADO,
    label: 'Extrato detalhado (transações)',
    description: 'Cada nota em linha — data, valor, pagamento e descrição (estilo extrato).',
  },
];

const getValidateScopeLabel = (validateScope) =>
  VALIDATE_SCOPE_OPTIONS.find((o) => o.id === validateScope)?.nome || 'Validado';

const resolveSegmentLabelForPreview = (filter, segmentLabel) => {
  if (!filter || !isSegmentFilterActive(filter.dim, filter.seg)) return null;
  if (segmentLabel) return segmentLabel;

  const dim = filter.dim;
  const seg = filter.seg;
  const staticOptions = STATIC_SEGMENT_OPTIONS[dim];
  const match = staticOptions?.find((o) => o.key === seg);
  return match?.label || seg;
};

export const isExportSecaoDisabled = (secaoValue, applyFilters, filter) =>
  Boolean(
    applyFilters
    && filter?.usuarioId
    && SECAO_RELATORIO_FINANCEIRO_OPTIONS.find((o) => o.value === secaoValue)
      ?.disableWhenUsuarioFilter,
  );

export const getDefaultExportSecoes = (applyFilters, filter) =>
  SECAO_RELATORIO_FINANCEIRO_OPTIONS.filter(
    (o) => o.value !== SECAO_RELATORIO_FINANCEIRO.DETALHADO
      && !isExportSecaoDisabled(o.value, applyFilters, filter),
  ).map((o) => o.value);

export const buildDefaultExportApiBody = () => normalizeFilterForApi(getDefaultFilter());

const buildExportFilterPreviewChips = ({
  filter,
  activeCategoria = null,
  usuarioNome = null,
  segmentLabel = null,
}) => {
  if (!filter) return [];

  const chips = [];
  const periodo = formatPeriodLabel(filter.inicio, filter.fim);
  chips.push(periodo ? `Período: ${periodo}` : 'Período completo');
  chips.push(`Escopo: ${getValidateScopeLabel(filter.validateScope)}`);

  if (filter.usuarioId) {
    chips.push(`Usuário: ${usuarioNome || 'Selecionado'}`);
  } else {
    chips.push('Todos os usuários');
  }

  if (activeCategoria) {
    chips.push(`Categoria: ${activeCategoria}`);
  }

  const segmentPreviewLabel = getSegmentFilterLabel(
    filter.dim,
    filter.seg,
    resolveSegmentLabelForPreview(filter, segmentLabel),
  );
  if (segmentPreviewLabel) {
    chips.push(segmentPreviewLabel);
  }

  if (filter.tipoPagamento) {
    chips.push(`Pagamento: ${getTipoPagamentoLabel(filter.tipoPagamento)}`);
  }

  if (filter.cartao) {
    chips.push('Cartão filtrado');
  }

  if (filter.inspecao != null && filter.inspecao !== '') {
    const isSim = filter.inspecao === true || String(filter.inspecao) === 'true';
    chips.push(`Inspeção: ${isSim ? 'Sim' : 'Não'}`);
  }

  return chips;
};

export const buildExportFilterPreview = ({
  filter,
  activeCategoria = null,
  applyFilters = true,
  usuarioNome = null,
  segmentLabel = null,
}) => {
  if (!applyFilters) {
    return buildExportFilterPreviewChips({
      filter: getDefaultFilter(),
    });
  }

  return buildExportFilterPreviewChips({
    filter,
    activeCategoria,
    usuarioNome,
    segmentLabel,
  });
};

export const buildExportPdfPayload = ({
  filter,
  secoes,
  activeCategoria = null,
  applyFilters = true,
}) => {
  if (!secoes?.length) return null;

  const body = applyFilters
    ? normalizeFilterForApi(filter)
    : buildDefaultExportApiBody();

  if (!body) return null;

  if (applyFilters && activeCategoria) {
    const def = resolveTipoNotaDef(null, activeCategoria);
    if (def?.enum) body.tipoNotas = [def.enum];
  }

  return { ...body, secoes };
};
